import { Button } from '@/components/ui/button'
import { DrawerClose, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { InputField } from '@/components/ui/input-field'
import { SelectField } from '@/components/ui/select-field'
import type { WorkspaceDomain } from 'honeygrid-types'
import { Globe, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface DomainRecordPanelProps {
	onClose: () => void
	domain?: WorkspaceDomain
	isNew?: boolean
}

export function DomainRecordPanel({ onClose, domain, isNew = false }: DomainRecordPanelProps) {
	const [domainType, setDomainType] = useState<'subdomain' | 'custom'>(domain?.type || 'subdomain')
	const [pattern, setPattern] = useState(domain?.pattern || '')
	const [isSaving, setIsSaving] = useState(false)

	const domainTypeOptions = [
		{
			value: 'subdomain',
			label: 'Subdomain',
			description: 'Use {workspace-slug}.tryhoneygrid.com',
		},
		{
			value: 'custom',
			label: 'Custom Domain',
			description: 'Use your own domain',
		},
	]

	const handleSave = async () => {
		if (!pattern.trim()) {
			toast.error('Pattern is required')
			return
		}

		setIsSaving(true)
		try {
			// Mock save - no backend integration yet
			await new Promise((resolve) => setTimeout(resolve, 500))
			toast.success(isNew ? 'Domain added' : 'Domain updated')
			onClose()
		} catch (error) {
			toast.error('Failed to save domain')
		} finally {
			setIsSaving(false)
		}
	}

	const handleCancel = () => {
		setPattern(domain?.pattern || '')
		setDomainType(domain?.type || 'subdomain')
		onClose()
	}

	return (
		<div className="flex flex-col h-full">
			<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
				<DrawerTitle>
					<div className="flex items-center gap-2">
						<Globe className="h-5 w-5" />
						{isNew ? 'Add Domain' : 'Edit Domain'}
					</div>
				</DrawerTitle>
				<DrawerClose asChild>
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="hover:bg-primary hover:text-primary-foreground"
					>
						<X className="h-4 w-4" />
					</Button>
				</DrawerClose>
			</DrawerHeader>

			<div className="flex-1 px-4 overflow-auto">
				<div className="space-y-6">
					<SelectField
						label="Domain Type"
						value={domainType}
						onChange={(value) => setDomainType(value as 'subdomain' | 'custom')}
						options={domainTypeOptions}
						disabled={isSaving}
						leftIcon={<Globe className="h-4 w-4" />}
					/>

					<InputField
						label={domainType === 'subdomain' ? 'Subdomain Pattern' : 'Custom Domain'}
						value={pattern}
						onChange={(e) => setPattern(e.target.value)}
						placeholder={domainType === 'subdomain' ? 'acme-corp' : 'www.example.com'}
						disabled={isSaving}
						subtext={domainType === 'subdomain'
							? 'Will create: {pattern}.tryhoneygrid.com'
							: 'Enter your custom domain name'}
						subtextVisibility='always'
					/>
					{!isNew && domain && (
						<>
							<InputField
								label="Verification Status"
								value={domain.verification_status}
								disabled
								readOnly
							/>
							{domain.is_primary && (
								<div className="text-sm text-muted-foreground">
									⭐ This is the primary domain
								</div>
							)}
						</>
					)}
				</div>
			</div>

			<DrawerFooter>
				<div className="flex gap-2">
					<Button onClick={handleSave} disabled={isSaving || !pattern.trim()}>
						{isSaving ? 'Saving...' : 'Save'}
					</Button>
					<Button variant="outline" onClick={handleCancel} disabled={isSaving}>
						Cancel
					</Button>
				</div>
			</DrawerFooter>
		</div>
	)
}
